import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FileText, Trash2, ArrowRight } from 'lucide-react';
import type { TournamentDraft } from '../engine/types';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  effectiveAgesFromTeamConfig,
  getTournamentAgeDisplayLines,
} from '@/utils/tournament-age';

interface ResumeDraftModalProps {
  isOpen: boolean;
  draft: TournamentDraft | null;
  onResume: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export function ResumeDraftModal({
  isOpen,
  draft,
  onResume,
  onDiscard,
  onClose,
}: ResumeDraftModalProps) {
  if (!draft) return null;

  const sportName = draft.sport?.sport?.name ?? 'Sin deporte';
  const tournamentName = draft.basicInfo?.name ?? 'Sin nombre';
  const lastUpdated = formatDistanceToNow(new Date(draft.updatedAt), {
    addSuffix: true,
    locale: es,
  });

  // Count completed steps
  const completedStepsCount = draft.completedSteps.length;

  const draftAges = effectiveAgesFromTeamConfig(draft.teamConfig ?? null);
  const ageLines = getTournamentAgeDisplayLines(
    draftAges.minAge,
    draftAges.maxAge,
  );

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <AlertDialogTitle className="text-center">
            Borrador encontrado
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            Tienes un torneo sin terminar. ¿Deseas continuar donde lo dejaste?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Draft info card */}
        <div className="my-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Deporte</span>
              <span className="font-medium text-gray-900">{sportName}</span>
            </div>
            {draft.basicInfo?.name && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Nombre</span>
                <span className="font-medium text-gray-900 truncate max-w-[180px]">
                  {tournamentName}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Progreso</span>
              <span className="font-medium text-gray-900">
                {completedStepsCount} pasos completados
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Última edición</span>
              <span className="text-sm text-gray-600">{lastUpdated}</span>
            </div>
            <div className="border-t border-gray-200 pt-2">
              <p className="text-xs font-medium text-gray-500">
                Restricción de edad
              </p>
              <div className="mt-1 space-y-0.5 text-xs text-gray-700">
                {ageLines.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <AlertDialogAction
            onClick={onResume}
            className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <ArrowRight className="h-4 w-4" />
            Continuar borrador
          </AlertDialogAction>
          <AlertDialogCancel
            onClick={onDiscard}
            className="mt-0 w-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Descartar y empezar de nuevo
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
